import type { RenderContext, VideoFrame } from '../../types';
import { BaseEffect } from '../base';
import { effectSeed, withOverlay } from './composite';
import { colorFromParams } from '../../utils/color';
import { WebGLShaderProgram } from '../../gpu/shader';
import { VERTEX_FULLSCREEN } from '../../shaders/glsl';
import { FRAG_SUN_RAYS } from '../../shaders/environmental';

const DEFAULTS = {
  intensity: 1.05,
  angle: 38,
  spread: 0.95,
  colorR: 1,
  colorG: 0.9,
  colorB: 0.62,
};

let sunProg: WebGLShaderProgram | null = null;

export class SunRaysEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'sun_rays', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!sunProg) sunProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_SUN_RAYS);
    const c = colorFromParams(params);
    const rad = (params.angle * Math.PI) / 180;
    return this.runShaderPass(frame, sunProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_intensity', params.intensity);
      prog.setUniform1f('u_spread', params.spread);
      prog.setUniform2f('u_direction', Math.cos(rad), Math.sin(rad));
      prog.setUniform3f('u_color', c.r, c.g, c.b);
      prog.setUniform1f('u_time', context.frameIndex / 30);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const c = colorFromParams(params);
    const rad = (params.angle * Math.PI) / 180;
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);
    const seed = effectSeed(context, this.id);
    return withOverlay(frame, (ctx, w, h) => {
      // Off-frame light so shafts span the full canvas
      const cx = w * (0.5 - dx * 0.85);
      const cy = h * (0.5 - dy * 0.85);
      const reach = Math.hypot(w, h) * (1.05 + params.spread * 0.35);
      const inten = params.intensity;

      // Full-frame atmospheric wash
      const wash = ctx.createRadialGradient(cx, cy, 0, cx, cy, reach);
      wash.addColorStop(0, `rgba(${c.r * 255},${c.g * 255},${c.b * 255},${0.42 * inten})`);
      wash.addColorStop(0.35, `rgba(${c.r * 255},${c.g * 255},${c.b * 255},${0.16 * inten})`);
      wash.addColorStop(0.7, `rgba(255,230,180,${0.06 * inten})`);
      wash.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = wash;
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = 'lighter';

      // Dense soft shafts across the whole frame
      const shafts = 28;
      for (let i = 0; i < shafts; i += 1) {
        const jitter = ((seed * 0.013 + i * 2.17) % 1) - 0.5;
        const a = rad + (i - shafts / 2) * (0.022 + params.spread * 0.012) + jitter * 0.014;
        const halfW = (4 + (i % 5) * 3.5) * (0.7 + params.spread * 0.6);
        const alpha = (0.08 + (i % 3) * 0.04) * inten;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(a);
        const lg = ctx.createLinearGradient(0, 0, reach, 0);
        lg.addColorStop(0, `rgba(255,245,210,${alpha * 1.4})`);
        lg.addColorStop(0.25, `rgba(255,220,150,${alpha})`);
        lg.addColorStop(0.65, `rgba(255,200,120,${alpha * 0.35})`);
        lg.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = lg;
        ctx.fillRect(0, -halfW, reach, halfW * 2);
        ctx.restore();
      }

      // Hot core bloom
      const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.min(w, h) * 0.35);
      core.addColorStop(0, `rgba(255,250,230,${0.55 * inten})`);
      core.addColorStop(0.4, `rgba(255,220,140,${0.2 * inten})`);
      core.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = core;
      ctx.fillRect(0, 0, w, h);
    }, 'lighter');
  }
}
